import { qreqsNeedClear } from 'components/utils';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { chooseBranch } from 'actions/storylet';
import { shouldFetch as shouldUpdateOpportunities } from 'actions/cards';
import useIsMounted from 'hooks/useIsMounted';
import BranchComponent from './BranchComponent';
import { Frequency, IBranch } from "types/storylet";
import { IAppState } from "types/app";

const MAX_ACTIVE_PLANS = 20;

type OwnProps = {
  branch: IBranch,
  defaultCursor?: boolean,
  dispatch: Function,
  isGoingBack?: boolean,
  onChooseBranch?: (_: any) => Promise<void>,
  storyletFrequency?: Frequency,
}

type Props = ReturnType<typeof mapStateToProps> & RouteComponentProps & OwnProps;

export interface State {
  isWorking: boolean,
  secondChanceIds: number[],
}

export function BranchContainer({
  actions,
  activePlans,
  branch,
  defaultCursor,
  dispatch,
  isChoosing,
  isGoingBack,
  onChooseBranch,
  storyletFrequency,
}: Props) {
  const {
    actionCost,
    challenges,
    currencyCost,
    currencyLocked,
    description,
    id,
    image,
    name,
    qualityLocked,
    qualityRequirements,
  } = branch;

  const ref = useRef<HTMLDivElement>(null);
  const [forceClearQreqs, setForceClearQreqs] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [secondChanceIds, setSecondChanceIds] = useState<number[]>([]);
  const isMounted = useIsMounted();

  const onResize = useCallback(() => {
    if (!ref.current) {
      return;
    }
    setForceClearQreqs(qreqsNeedClear(ref.current));
  }, []);

  useEffect(() => {
    window.addEventListener('resize', onResize);
    onResize();
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [onResize]);

  const handleChooseBranch = useCallback(async () => {
    const { qualityRequirements, id: branchId } = branch;
    if (storyletFrequency === 'Sometimes') {
      dispatch(shouldUpdateOpportunities());
    }

    setIsWorking(true);

    if (onChooseBranch) {
      await onChooseBranch({ branchId, qualityRequirements, secondChanceIds });
    } else {
      await dispatch(chooseBranch({ branchId, qualityRequirements, secondChanceIds }));
    }

    if (isMounted.current) {
      setIsWorking(false);
    }
  }, [
    branch,
    dispatch,
    isMounted,
    onChooseBranch,
    secondChanceIds,
    storyletFrequency,
  ]);

  const handleToggleSecondChance = useCallback((checked: boolean, id: number) => {
    if (checked) {
      setSecondChanceIds([...secondChanceIds, id]);
      return;
    }
    const newIds = secondChanceIds.filter(e => e !== id);
    setSecondChanceIds(newIds);
  }, [secondChanceIds]);

  const disabled = isChoosing || isWorking;

  // Compare actions to action cost directly
  const isLocked = (actions < actionCost) || currencyLocked || qualityLocked;

  // Is a player maxed out on active plans?
  const playerHasMaximumActivePlans = activePlans && (activePlans.length >= MAX_ACTIVE_PLANS);

  return (
    <BranchComponent
      branch={branch}
      challenges={challenges}
      currencyCost={currencyCost}
      defaultCursor={defaultCursor ?? false}
      description={description}
      disabled={disabled}
      forceClearQreqs={forceClearQreqs}
      id={id}
      image={image}
      isLocked={isLocked}
      name={name}
      playerHasMaximumActivePlans={playerHasMaximumActivePlans}
      qualityRequirements={qualityRequirements}
      isGoingBack={isGoingBack ?? false}
      isWorking={isWorking}
      onChooseBranch={handleChooseBranch}
      onToggleSecondChance={handleToggleSecondChance}
    />
  );
}

BranchContainer.displayName = 'BranchContainer';

const mapStateToProps = ({
  actions: { actions },
  plans: { activePlans },
  storylet: { isChoosing },
}: IAppState) => ({
  actions,
  activePlans,
  isChoosing,
});

export default withRouter(connect(mapStateToProps)(BranchContainer));
